import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { formatApiError } from "@/lib/api";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Droplets, Mail, Lock, User, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", mill_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);
  useEffect(() => { if (params.get("error") === "oauth") setError("Google sign-in failed. Please try again."); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, mill_name: form.mill_name });
      toast.success(t("welcome_back"));
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden lg:flex lg:w-1/2 hero-radial grain-bg flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Droplets className="h-5 w-5" />
          </div>
          <span className="font-heading text-2xl font-bold">e-olive Albania</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">{t("tagline")}</h2>
          <p className="mt-4 max-w-md text-muted-foreground">{t("hero_sub")}</p>
        </div>
        <p className="text-sm text-muted-foreground">{t("footer_note")}</p>
      </div>

      <div className="flex w-full flex-col lg:w-1/2">
        <div className="flex items-center justify-end gap-2 p-6">
          <LangToggle /><ThemeToggle />
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-12">
          <div className="w-full max-w-sm animate-fade-up">
            <Link to="/" className="mb-6 flex items-center gap-2 lg:hidden">
              <Droplets className="h-6 w-6 text-primary" />
              <span className="font-heading text-xl font-bold">e-olive Albania</span>
            </Link>
            <h1 className="font-heading text-3xl font-bold">{mode === "login" ? t("welcome_back") : t("create_account")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{mode === "login" ? t("login_sub") : t("signup_sub")}</p>

            <Button
              data-testid="google-login-btn"
              type="button"
              variant="outline"
              onClick={googleLogin}
              className="mt-6 w-full rounded-lg border-border"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
              {t("google_login")}
            </Button>

            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{t("or_continue")}</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "signup" && (
                <>
                  <Field icon={User} label={t("name")} testid="name-input" value={form.name} onChange={set("name")} required />
                  <Field icon={Building2} label={t("mill_name")} testid="millname-input" value={form.mill_name} onChange={set("mill_name")} placeholder={`(${t("optional")})`} />
                </>
              )}
              <Field icon={Mail} label={t("email")} testid="email-input" type="email" value={form.email} onChange={set("email")} required />
              <Field icon={Lock} label={t("password")} testid="password-input" type="password" value={form.password} onChange={set("password")} required />

              {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}

              <Button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full rounded-lg bg-primary hover:bg-olive-hover">
                {busy ? t("loading") : mode === "login" ? t("login") : t("signup")}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {mode === "login" ? t("no_account") : t("have_account")}{" "}
              <button
                data-testid="toggle-mode-btn"
                onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
                className="font-semibold text-primary hover:underline"
              >
                {mode === "login" ? t("signup") : t("login")}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid={testid} className="pl-9" {...props} />
      </div>
    </div>
  );
}
